import { RegisterOptions } from 'react-hook-form';

import { IFormState, TNameOfFormState } from './form';

type TValidationRules = Pick<
  RegisterOptions<IFormState, keyof IFormState>,
  'max' | 'maxLength' | 'min' | 'minLength' | 'pattern' | 'required' | 'validate'
>;

export enum EElementCategory {
  /** Группа, например "Данные кредита" **/
  Group = 'group',
  /** Любое поле **/
  Field = 'field',
  /** Какой-то статический элемент **/
  Static = 'static',
}

export enum EFieldType {
  /** Инпут чипсов (радио инпут в виде кнопок) **/
  Chips = 'chips',
  /** Инпут переключатель **/
  Switch = 'switch',
  /** Радио инпут (стандартный вид) **/
  Radio = 'radio',
  /** Инпут с выпадающим списком вариантов **/
  Select = 'select',
  /** Инпут с выпадающим списком вариантов и возможностью поиска **/
  Suggest = 'suggest',
  /** Числовой инпут **/
  InputNumber = 'InputNumber',
  /** Числовой инпут с выбором вариантов **/
  InputWithOptions = 'InputWithOptions',
  /** Двойной инпут для ввода лет и месяцев (возвращает сумму "лет + месяцы") **/
  YearMonthField = 'YearMonthField',
}

export enum EStaticType {
  /** Кнопка открывающая попап с информацией о программах **/
  ProgramFeaturesButton = 'ProgramFeaturesButton',
  /** Кнопка открывающая все фильтры **/
  MoreFiltersButton = 'MoreFiltersButton',
}

export enum ECustomStaticName {
  /** Кнопка открывающая попап с информацией о программах **/
  ProgramFeaturesButton = 'ProgramFeaturesButton',
}

export interface IElement {
  /** Категория элемента **/
  category: EElementCategory;
  /** Описание, например "Отказ увеличивает ставку на 1%" **/
  description?: string;
  /** Отступ элемента */
  offset?: IElementOffset;
}

/** Значения полей **/
export interface IEFieldSource<TYPE = string> {
  value: TYPE;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
  checked?: boolean;
  description?: string;
  dataTestId?: string;
}

/** Функция для рассчета значения на основе стейта формы **/
export type TComputed<TYPE> = (state: IFormState) => TYPE;

/** Состояние поля **/
export interface IField<TYPE = string> {
  /** Заголовок, например "Тип расчета" **/
  label?: string;
  /** Название поля **/
  name: TNameOfFormState;
  /** Поле задизейблено **/
  disabled?: boolean;
  /** Поле скрыто **/
  hidden?: boolean;
  /** Варианты для значений поля **/
  source?: IEFieldSource<TYPE>[];
  /** Плейсхолдер **/
  placeholder?: string | string[];
  /** Присоединяется в конце поля (для Input) **/
  rightAdornment?: string | string[];
  /** decimalDigits (для InputNumber) количество знаков после запятой **/
  decimalDigits?: number;
  /** Значение по умолчанию **/
  defaultValue?: TYPE | boolean;
  /** Тип поля */
  type: EFieldType;
  /** calculateForField функция для расчета значения Input в компонентах InputWithOptions (например "Первоначальный взнос") **/
  calculateForField?: TCalculateFunc;
  /** calculateForChips функция для расчета значения Chips в компонентах InputWithOptions (например "Первоначальный взнос") **/
  calculateForChips?: TCalculateFunc;
  /** Валидация поля (required, validate и т.д.) */
  validation?: TValidationRules;
  /** Tooltip (для Switch) **/
  tooltip?: string;
  /** Вариант отображения (для Radio, Chips) **/
  variant?: 'radioCell' | 'withProgramGuide';
}

/** Вычисляемое состояние поля **/
export interface IFieldComputed<TYPE = string> {
  /** Заголовок, например "Тип расчета" **/
  label?: string;
  /** Название поля **/
  name: TNameOfFormState;
  /** Поле задизейблено **/
  disabled?: boolean | TComputed<boolean>;
  /** Поле скрыто **/
  hidden?: boolean | TComputed<boolean>;
  /** Варианты для значений поля **/
  source?: IEFieldSource<TYPE>[] | TComputed<IEFieldSource<TYPE>[]>;
  /** Плейсхолдер **/
  placeholder?: string | string[] | TComputed<string>;
  /** Присоединяется в конце поля (для Input) **/
  rightAdornment?: string | string[] | TComputed<string>;
  /** decimalDigits (для InputNumber) количество знаков после запятой **/
  decimalDigits?: number;
  /** Значение по умолчанию **/
  defaultValue?: TYPE | boolean;
  /** calculateForField функция для расчета значения Input в компонентах InputWithOptions (например "Первоначальный взнос") **/
  calculateForField?: TCalculateFunc;
  /** calculateForChips функция для расчета значения Chip в компонентах InputWithOptions (например "Первоначальный взнос") **/
  calculateForChips?: TCalculateFunc;
  /** Валидация поля (required, validate и т.д.) */
  validation?: TValidationRules;
  /** Tooltip (для Switch) **/
  tooltip?: string;
  /** Вариант отображения (для Radio, Chips) **/
  variant?: 'radioCell' | 'withProgramGuide';
}

export type TCalculateFunc = (value: number, state: IFormState) => number;

export interface IElementOffset {
  bottom?: number;
}

/** Элемент с типом поле **/
export interface IFieldElement<TYPE = string> extends IElement, IFieldComputed<TYPE> {
  category: EElementCategory.Field;
  /** Тип поля **/
  type: EFieldType;
}

/** Группа полей **/
export interface IGroup extends IElement {
  label?: string;
  category: EElementCategory.Group;
  elements: TElement[];
}

/** Статический элемент формы, не поле (к примеру, кнопка вызова попапа) **/
export interface IStaticElement extends IElement {
  label: string;
  category: EElementCategory.Static;
  type: EStaticType;
}

export type TElement = IGroup | IFieldElement | IStaticElement;

/** Финальная схема формы **/
export interface IScheme {
  elements: TElement[];
}
